import React from 'react';
import { Slider } from '@ui-component-library';
import * as Opt from '../editor/Helpers';
import MediaSelector from '../editor/fields/MediaSelector';
import ColorSelector from '../editor/fields/ColorSelector';
import TextEditor from '../editor/fields/TextEditor';

const NUMTESTIMONIALSMOBILE = 8;
const DESKTOP_VISIBLE = 3;

// Tailwind text sizes from text-sizes-mapping: h4, fs-5, fs-6
const h4Classes = 'text-[25px] leading-[1.1] tracking-[-0.75px] md:text-[49px] md:leading-[1.05] md:tracking-[-1.47px]';
const fs5Classes = 'text-[20px] leading-[1.2] tracking-[-0.4px]';
const fs6Classes = 'text-[16px] leading-[1.25] tracking-[-0.32px]';

function TestimonialCard({ item, className = '' }) {
  return (
    <div className={`m-[20px] p-[20px] rounded-2xl border-0 bg-${item.backgroundItemColor} ${className}`}>
      <div className="flex flex-col h-full">
        <div className="block quill-text mb-[30px]" dangerouslySetInnerHTML={{ __html: item.testimonial }} />
        <div className="mt-auto">
          <p className={`mb-0 ${fs6Classes} font-medium ${item.nameColor}`}>{item.name}</p>
          <p className={`mb-0 ${fs6Classes} ${item.companyColor}`}>{item.company}</p>
        </div>
      </div>
    </div>
  );
}

class CustomerTestimonials extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeItem: 0,
      activeGroup: 0,
    };
    this.mobileSliderRef = React.createRef();
    this.desktopSliderRef = React.createRef();
  }

  handleNextMobile = () => {
    this.mobileSliderRef.current?.next();
  };

  handlePrevMobile = () => {
    this.mobileSliderRef.current?.prev();
  };

  handleNextDesktop = () => {
    const nextPage = Math.min(Math.ceil(this.props.items.length / DESKTOP_VISIBLE) - 1, this.state.activeGroup + 1);
    this.desktopSliderRef.current?.scrollTo(nextPage * DESKTOP_VISIBLE);
  };

  handlePrevDesktop = () => {
    const prevPage = Math.max(0, this.state.activeGroup - 1);
    this.desktopSliderRef.current?.scrollTo(prevPage * DESKTOP_VISIBLE);
  };

  render() {
    const visibilityClasses = window.location.pathname.includes('editor') && ['dt', 'mob'].filter((i) => i === this.props.visibility).length
      ? 'opacity-50 flex'
      : this.props.visibility === 'dt'
        ? 'hidden lg:flex'
        : this.props.visibility === 'mob'
          ? 'block lg:hidden'
          : 'flex';

    return (
      <div className={`${visibilityClasses} p-0 bg-${this.props.backgroundColor}`}>
        <div className="container">
          <div className="flex flex-col lg:flex-row items-end justify-between">
            <div className="w-full lg:w-5/6">
              <p className={`${fs6Classes} font-medium mb-[20px] ${this.props.subtitleColor}`}>{this.props.subtitle}</p>
              <h4 className={`mb-[.5rem] ${h4Classes} ${this.props.headerColor}`}>{this.props.header}</h4>
              <p className={`${fs5Classes} mb-[30px] lg:mb-0 ${this.props.bodyColor}`}>{this.props.body}</p>
            </div>
            {this.props.showButton === 'yes' && (
              <div className="w-full lg:w-1/6 mt-[10px] lg:mt-[30px] mb-[40px]">
                <a href={this.props.href} className={`n-btn ${this.props.buttonSize} ${this.props.buttonStyle} ${this.props.buttonOptions}`}>
                  {this.props.text}
                  <span>{this.props.iconAfter && <img className={`ml-1 ${this.props.iconColor}`} style={{ width: '18px', height: '18px' }} src={this.props.iconAfter} />}</span>
                </a>
              </div>
            )}
          </div>

          {/* Mobile Slider */}
          <div className="w-full block lg:hidden">
            <Slider
              ref={this.mobileSliderRef}
              mobileVisible={1}
              desktopVisible={3}
              onIndexChange={(index) => this.setState({ activeItem: index })}
            >
              {this.props.items.slice(0, NUMTESTIMONIALSMOBILE).map((item, index) => (
                <TestimonialCard key={index} item={item} />
              ))}
            </Slider>

            {this.props.items.length > 1 && (
              <div className="w-full flex justify-center items-center mt-[30px]">
                <div className="rounded-full border-2 border-neutral-charcoal flex mr-[10px] items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handlePrevMobile}>
                  <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Previous" />
                </div>
                {this.props.items.slice(0, NUMTESTIMONIALSMOBILE).map((_, index) => (
                  <div key={index} role="button" tabIndex={0} onClick={() => this.mobileSliderRef.current?.scrollTo(index)} className={`border-2 border-neutral-charcoal rounded-full mx-2 h-2.5 w-2.5 transition-[.6s] cursor-pointer ${this.state.activeItem === index ? 'bg-neutral-charcoal' : ''}`} />
                ))}
                <div className="rounded-full border-2 border-neutral-charcoal flex ml-[10px] items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handleNextMobile}>
                  <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Next" style={{ transform: 'rotate(180deg)' }} />
                </div>
              </div>
            )}
          </div>

          {/* Desktop Slider - 3 items per slide */}
          <div className="mt-[30px] w-full hidden lg:block">
            <Slider
              ref={this.desktopSliderRef}
              mobileVisible={1}
              desktopVisible={DESKTOP_VISIBLE}
              onIndexChange={(index) => this.setState({ activeGroup: Math.floor(index / DESKTOP_VISIBLE) })}
            >
              {this.props.items.map((item, index) => (
                <TestimonialCard key={index} item={item} className="flex-1 min-h-0 m-3" />
              ))}
            </Slider>

            <div className="mt-3 flex items-center justify-between">
              <div className="flex justify-start">
                {Array.from({ length: Math.ceil(this.props.items.length / DESKTOP_VISIBLE) }).map((_, pageIndex) => (
                  <div key={pageIndex} role="button" tabIndex={0} onClick={() => this.desktopSliderRef.current?.scrollTo(pageIndex * DESKTOP_VISIBLE)} className={`border-2 border-neutral-charcoal rounded-full mx-[10px] h-4 w-4 transition-[.6s] cursor-pointer ${this.state.activeGroup === pageIndex ? 'bg-neutral-charcoal' : ''}`} />
                ))}
              </div>

              {Math.ceil(this.props.items.length / DESKTOP_VISIBLE) > 1 && (
                <div className="flex justify-end">
                  <div className="rounded-full border-2 border-neutral-charcoal flex mr-[10px] items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handlePrevDesktop}>
                    <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Previous" />
                  </div>
                  <div className="rounded-full border-2 border-neutral-charcoal flex ml-[10px] items-center justify-center w-12 h-12 cursor-pointer" role="button" onClick={this.handleNextDesktop}>
                    <img src={`${Window.static_path}img/icons/arrowBlack.svg`} height="16px" className="neutral-charcoal-filter" alt="Next" style={{ transform: 'rotate(180deg)' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CustomerTestimonials.defaultProps = {
  visibility: "",
  backgroundColor: 'white-50',
  subtitle: 'Referenser',
  subtitleColor: 'neutral-charcoal',
  header: 'Customer testimonials',
  headerColor: 'neutral-charcoal',
  body: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
  bodyColor: 'neutral-charcoal',
  showButton: "yes",
  text: "Action",
  href: "/",
  buttonSize: "",
  buttonStyle: "btn-outline",
  buttonOptions: "btn-charcoal",
  iconAfter: null,
  iconColor: "neutral-charcoal-filter",
  items: [
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"ONE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"TWO Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"THREE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"FOUR Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"FIVE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"SIX Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"SEVEN Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"EIGHT Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
    {
      backgroundItemColor: "neutral-cloud",
      testimonial: '"NINE Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat."',
      name: 'Name Surname',
      nameColor: 'neutral-charcoal',
      company: 'Position, Company name',
      companyColor: 'neutral-charcoal',
    },
  ]

}

CustomerTestimonials.fields = {
  backgroundColor: {
    type: "custom",
    render: (props) => <ColorSelector {...props} />
  },
  visibility: {
    type: "select",
    options: Opt.visibilityOptions
  },
  subtitle: { type: "text" },
  subtitleColor: {
    type: "select",
    options: Opt.colorVariables
  },
  header: { type: "text" },
  headerColor: {
    type: "select",
    options: Opt.colorVariables
  },
  body: { type: "text" },
  bodyColor: {
    type: "select",
    options: Opt.colorVariables
  },
  showButton: {
    type: "select",
    options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]
},
  text: { type: "text" },
  href: { type: "text" },
  buttonSize: {
    type: "select",
    options: Opt.buttonSize
  },
  buttonStyle: {
    type: "select",
    options: Opt.buttonStyle
  },
  buttonOptions: {
    type: "select",
    options: Opt.buttonOptions
  },
  iconAfter: {
    type: "custom",
    render: ({ name, onChange, value }) => <MediaSelector name={'iconAfter'} type={'icon'} onChange={onChange} value={value} />,
  },
  iconColor: {
    type: "select",
    options: Opt.filterOptions
  },
  items: {
    type: "array",
    arrayFields: {
      backgroundItemColor: {
        type: "select",
        options: Opt.colorVariables
      },
      testimonial: { 
        type: "custom",
        render: ({ name, onChange, value }) => <TextEditor name={name} value={value} onChange={onChange} /> 
      },
      name: { type: "text" },
      nameColor:  {
        type: "select",
        options: Opt.colorVariables
      },
      company: { type: "text" },
      companyColor:  {
        type: "select",
        options: Opt.colorVariables
      },
      
    },
    defaultItemProps: CustomerTestimonials.defaultProps.items[0],
  },
}

export default CustomerTestimonials;